/**
 * View Transaction Dialogue
 * Card-based full detail view of a single transaction
 * Matches the style of CapsTransactions.jsx detail dialog
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, Chip, Button, Card, CardContent,
  Alert, IconButton,
  useTheme, alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  TRANSACTION_TYPE_MAP,
  formatTransactionDate,
  formatCurrency
} from './transactionConstants';

// Format a YYYY-MM-DD date string in local timezone (avoids UTC off-by-one)
function formatDateOnly(dateStr) {
  if (!dateStr) return 'N/A';
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts;
  if (isNaN(y) || isNaN(m) || isNaN(d)) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString();
}

function calculateTotalCost(tx) {
  const unitCost = parseFloat(tx.unit_cost) || 0;
  const taxAmount = parseFloat(tx.tax_amount) || 0;
  const shippingCost = parseFloat(tx.shipping_amount || tx.shipping_cost) || 0;
  const quantity = Math.abs(parseFloat(tx.quantity)) || 0;
  return (unitCost * quantity) + taxAmount + shippingCost;
}

const cardSx = (theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: 'white',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'default',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
  }
});

export default function ViewTransactionDialogue({
  open,
  onClose,
  transaction,
  config,
  timeFormat,
  onEdit
}) {
  const theme = useTheme();

  if (!transaction) return null;

  const tx = transaction;
  const typeInfo = TRANSACTION_TYPE_MAP[tx.transaction_type];
  const itemName = tx[config?.tableName]?.name || 'Unknown';
  const itemSku = tx[config?.tableName]?.sku || '';
  const itemUnit = tx[config?.tableName]?.primary_unit_type || tx[config?.tableName]?.unit || config?.singularName?.toLowerCase() || 'units';
  const itemMeasure = tx[config?.tableName]?.primary_unit_measure || null;

  const qty = parseFloat(tx.quantity);
  const isNegative = qty < 0;
  const totalQty = tx.total_quantity != null ? parseFloat(tx.total_quantity) : null;

  const isPurchase = tx.transaction_type === 'purchase';
  const isUsage = tx.transaction_type === 'usage';
  const isTransfer = tx.transaction_type === 'transfer';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          maxHeight: '90vh',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          bgcolor: alpha(theme.palette.grey[50], 0.95),
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        pb: 2,
        pt: 3,
        px: 3,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            {typeInfo?.icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Transaction Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              {typeInfo?.label || tx.transaction_type} - {formatDateOnly(
                (tx.transaction_date || tx.created_at || '').split('T')[0]
              )}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.grey[400], 0.1), color: 'text.primary' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>

            {/* Transaction Information Card */}
            <Grid item xs={12}>
              <Card sx={cardSx(theme)}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Transaction Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Type
                        </Typography>
                        {typeInfo ? (
                          <Chip
                            icon={typeInfo.icon}
                            label={typeInfo.label}
                            color={typeInfo.color}
                            size="medium"
                            sx={{
                              fontWeight: 500,
                              border: `1px solid`,
                              borderColor: theme.palette[typeInfo.color]?.main || theme.palette.primary.main
                            }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {tx.transaction_type || 'Unknown'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Date & Time
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatTransactionDate(tx.transaction_date || tx.created_at, timeFormat)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Quantity Change
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 500, color: isNegative ? 'warning.main' : 'success.main' }}
                        >
                          {isNegative ? '' : '+'}{Math.abs(qty).toLocaleString()} {itemUnit}
                        </Typography>
                        {totalQty != null && itemMeasure && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                            ({isNegative ? '' : '+'}{Math.abs(totalQty).toLocaleString()} {itemMeasure})
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Item Details + Location Details (side by side) */}
            <Grid item xs={12} md={6}>
              <Card sx={{ ...cardSx(theme), height: '100%', minHeight: '200px' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    {config?.singularName || 'Item'} Details
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                      {config?.singularName || 'Item'} Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {itemName}
                    </Typography>
                  </Box>
                  {itemSku && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        SKU
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {itemSku}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                      Unit Type
                    </Typography>
                    <Chip
                      label={itemUnit}
                      size="medium"
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        fontWeight: 500
                      }}
                    />
                  </Box>
                  {/* Lot/Batch info for creation, usage, transfer, adjustment, deletion (deletion only if values exist) */}
                  {(tx.transaction_type === 'inventory_creation' || isUsage || isTransfer || tx.transaction_type === 'adjustment' || (tx.transaction_type === 'inventory_deletion' && (tx.lot_number || tx.batch_number))) && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Lot Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {tx.lot_number || 'Not specified'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Batch Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {tx.batch_number || 'Not specified'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ ...cardSx(theme), height: '100%', minHeight: '200px' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Location Details
                  </Typography>
                  {isTransfer ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          From Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {tx.from_location?.name || 'Unknown Location'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          To Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {tx.to_location?.name || 'Unknown Location'}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Location
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {tx.location?.name || 'Unknown Location'}
                      </Typography>
                    </Box>
                  )}
                  {tx.supplier && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Vendor
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {tx.supplier.name}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Purchase Order Details (purchase transactions only) */}
            {isPurchase && (
              <Grid item xs={12}>
                <Card sx={cardSx(theme)}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                      Purchase Order Details
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Row 1: Vendor, PO Number, PO Date, Invoice Number */}
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Vendor
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {tx.supplier?.name || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            PO Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {tx.po_number || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            PO Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDateOnly(tx.po_date)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Invoice Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {tx.invoice_number || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Row 2: Payment Status, Delivery Status, Invoice Date, Invoice Due Date */}
                      {tx.payment_status && (
                        <Grid item xs={12} md={3}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Payment Status
                            </Typography>
                            <Chip
                              label={tx.payment_status.charAt(0).toUpperCase() + tx.payment_status.slice(1)}
                              color={tx.payment_status === 'paid' ? 'success' : tx.payment_status === 'partial' ? 'warning' : 'error'}
                              size="medium"
                              sx={{
                                fontWeight: 500,
                                border: `1px solid`,
                                borderColor: tx.payment_status === 'paid'
                                  ? theme.palette.success.main
                                  : tx.payment_status === 'partial'
                                    ? theme.palette.warning.main
                                    : theme.palette.error.main
                              }}
                            />
                          </Box>
                        </Grid>
                      )}
                      {tx.delivery_status && (
                        <Grid item xs={12} md={3}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Delivery Status
                            </Typography>
                            <Chip
                              label={tx.delivery_status.charAt(0).toUpperCase() + tx.delivery_status.slice(1)}
                              color={
                                tx.delivery_status === 'delivered' ? 'success' :
                                tx.delivery_status === 'shipped' ? 'warning' :
                                tx.delivery_status === 'cancelled' ? 'error' : 'info'
                              }
                              size="medium"
                              sx={{
                                fontWeight: 500,
                                border: `1px solid`,
                                borderColor:
                                  tx.delivery_status === 'delivered' ? theme.palette.success.main :
                                  tx.delivery_status === 'shipped' ? theme.palette.warning.main :
                                  tx.delivery_status === 'cancelled' ? theme.palette.error.main : theme.palette.info.main
                              }}
                            />
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Invoice Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDateOnly(tx.invoice_date)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Invoice Due Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDateOnly(tx.invoice_due_date)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Row 3: Unit Cost, Tax Amount, Shipping Cost, Total Cost */}
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Unit Cost
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatCurrency(tx.unit_cost)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Tax Amount
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatCurrency(tx.tax_amount)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Shipping Cost
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatCurrency(tx.shipping_amount || tx.shipping_cost)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Total Cost
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatCurrency(calculateTotalCost(tx))}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Batch & Quality Info */}
                      <Grid item xs={12}>
                        <Box sx={{ pt: 2, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}` }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                            Batch & Quality Information
                          </Typography>
                          <Grid container spacing={2}>
                            {tx.batch_number && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Batch Number
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {tx.batch_number}
                                </Typography>
                              </Grid>
                            )}
                            {tx.lot_number && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Lot Number
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {tx.lot_number}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Usage Alert */}
            {isUsage && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <strong>Usage Record:</strong> {Math.abs(qty).toLocaleString()} {itemUnit}{totalQty != null && itemMeasure ? ` (${Math.abs(totalQty).toLocaleString()} ${itemMeasure})` : ''} were consumed from inventory.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Notes */}
            {tx.notes && (
              <Grid item xs={12}>
                <Card sx={cardSx(theme)}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                      Additional Information
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Notes
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                        {tx.notes}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, bgcolor: 'white' }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 2, fontWeight: 500, color: 'text.secondary' }}
        >
          Close
        </Button>
        {onEdit && (
          <Button
            variant="outlined"
            onClick={() => { onClose(); onEdit(tx); }}
            startIcon={<EditIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                border: `1px solid ${theme.palette.primary.main}`,
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            Edit Transaction
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
